import { ProfileView, ProfileViewWithShadowBan } from '../../../../api/client'

export type ProfileWithOptionalName = (ProfileView | ProfileViewWithShadowBan) & {
    name?: string | null
}

export type ProfileContentData = {
    serviceAuthorName?: string | null
    serviceAuthorDisplayUsername?: string | null
    socialMediaServiceName?: string | null
}

type Props = {
    profile: ProfileWithOptionalName
    contentData?: ProfileContentData
    loading?: boolean
}

/**
 * Component to display a profile's name with fallbacks and loading state.
 */
export default function ProfileNameDisplay({ profile, contentData, loading }: Props) {
    const displayName = getProfileDisplayName(profile, contentData)

    if (displayName) {
        return <>{displayName}</>
    }

    if (loading) {
        return <span className="text-gray-400">Loading...</span>
    }

    const tooltipMessage =
        "We were unable to retrieve this profile's name or username from the social network."

    return (
        <span
            className="cursor-help"
            data-tooltip-id="tooltip-id"
            data-tooltip-content={tooltipMessage}
        >
            Unknown user name
        </span>
    )
}

/**
 * Resolves the best display name for a profile, using fallbacks from content data if needed.
 * Returns null if no valid name can be determined.
 *
 * During content collection some of the platforms do not supply a username and we substitute "unknown" when creating a Profile.
 */
export function getProfileDisplayName(
    profile: ProfileWithOptionalName | null,
    contentData?: ProfileContentData
): string | null {
    if (!profile) return null

    const trimmedUsername = (profile.username ?? '').trim()
    const hasValidUsername =
        trimmedUsername.length > 0 && trimmedUsername.toLowerCase() !== 'unknown'

    if (hasValidUsername) {
        return trimmedUsername
    }

    const trimmedName = profile.name?.trim()
    if (trimmedName) {
        return trimmedName
    }

    // Try to get name from content if available.
    const contentAuthorUsername = contentData?.serviceAuthorDisplayUsername?.trim()
    if (contentAuthorUsername && contentAuthorUsername.toLowerCase() !== 'unknown') {
        return contentAuthorUsername
    }

    const contentAuthorName = contentData?.serviceAuthorName?.trim()
    if (contentAuthorName && contentAuthorName.toLowerCase() !== 'unknown') {
        return contentAuthorName
    }

    return null
}
